import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, QueryStatus } from "@cx/ui";
import type { CommissionSummaryResult } from "@cx/contracts";
import { formatDateBR, todayInSaoPaulo } from "@cx/domain";
import { useSession } from "../../session";
import { COMMISSION_BASE_LABEL } from "../sellers/labels";
import { money, monthPeriod } from "./labels";

/** Comissões (RF-VEN-08): resumo da conta-corrente por vendedor no mês. */
export function CommissionsPage(): ReactElement {
  const { companyId, api } = useSession();
  const search = useSearch({ from: "/vendas/comissoes" });
  const navigate = useNavigate({ from: "/vendas/comissoes" });
  const month = search.month ?? todayInSaoPaulo().slice(0, 7);
  const [idle, setIdle] = useState(false);
  const period = monthPeriod(month);
  const summary = useQuery({
    queryKey: ["commissions", companyId, "summary", month, idle],
    queryFn: () =>
      api<CommissionSummaryResult>(
        `/commissions?${new URLSearchParams({
          ...(period ?? {}),
          ...(idle ? { includeIdle: "true" } : {}),
        })}`,
      ),
  });
  const rows = summary.data?.data ?? [];

  return (
    <>
      <ContentHeader title="Comissões" breadcrumb={["Vendas", "Comissões"]} />
      <div className="box card">
        <div className="row g-2 p-3 pb-0 align-items-end">
          <div className="col-7 col-md-3">
            <label className="form-label small" htmlFor="com-month">
              Mês
            </label>
            <input
              id="com-month"
              type="month"
              className="form-control"
              value={month}
              onChange={(e) => navigate({ search: { month: e.target.value || undefined }, replace: true })}
            />
          </div>
          <div className="col-12 col-md-5">
            <div className="form-check mb-2">
              <input
                id="com-idle"
                type="checkbox"
                className="form-check-input"
                checked={idle}
                onChange={(e) => setIdle(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="com-idle">
                Listar vendedores sem movimentação
              </label>
            </div>
          </div>
        </div>
        {summary.data && (
          <p className="text-muted small px-3 pt-2 mb-0">
            Período de {formatDateBR(summary.data.from)} a {formatDateBR(summary.data.to)}. Comissões
            pendentes ainda não foram liberadas (base recebido) e não entram no saldo.
          </p>
        )}
        <QueryStatus loading={summary.isLoading} error={summary.error} />
        {summary.isSuccess && rows.length === 0 && (
          <p className="text-muted p-3 mb-0">Nenhum vendedor com movimentação no período.</p>
        )}
        {rows.length > 0 && (
          <div className="table-responsive mt-2">
            <table className="table align-middle mb-0" aria-label="Comissões por vendedor">
              <thead>
                <tr>
                  <th scope="col">Vendedor</th>
                  <th scope="col">Base</th>
                  <th scope="col" className="text-end">
                    Faturado
                  </th>
                  <th scope="col" className="text-end">
                    Saldo anterior
                  </th>
                  <th scope="col" className="text-end">
                    (+) Comissões
                  </th>
                  <th scope="col" className="text-end">
                    (+) Outros créditos
                  </th>
                  <th scope="col" className="text-end">
                    (−) Outros débitos
                  </th>
                  <th scope="col" className="text-end">
                    (−) Pagamentos
                  </th>
                  <th scope="col" className="text-end">
                    (*) Pendentes
                  </th>
                  <th scope="col" className="text-end">
                    (=) Saldo
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.sellerId}>
                    <td>
                      <Link
                        to="/vendas/comissoes/$sellerId"
                        params={{ sellerId: r.sellerId }}
                        search={{ month }}
                      >
                        {r.sellerName}
                      </Link>
                      {r.deleted && <span className="badge text-bg-secondary ms-2">Excluído</span>}
                    </td>
                    <td>{COMMISSION_BASE_LABEL[r.base]}</td>
                    <td className="text-end">{money(r.invoicedCents)}</td>
                    <td className="text-end">{money(r.openingCents)}</td>
                    <td className="text-end">{money(r.commissionsCents)}</td>
                    <td className="text-end">{money(r.creditsCents)}</td>
                    <td className="text-end">{money(r.debitsCents)}</td>
                    <td className="text-end">{money(r.paymentsCents)}</td>
                    <td className="text-end text-muted">{money(r.pendingCents)}</td>
                    <td
                      className={`text-end fw-semibold${(r.closingCents ?? 0) < 0 ? " text-danger" : ""}`}
                      data-testid={`closing-${r.sellerName}`}
                    >
                      {money(r.closingCents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
